import React, { useMemo, useRef, useState } from 'react'
import { Box, Button, Card, HStack, Icon, IconButton, Image, Text, VStack, Tooltip } from '@chakra-ui/react'
import { FiChevronLeft, FiChevronRight, FiEdit2 } from 'react-icons/fi'
import { useCachedImage } from '../../hooks/useCachedImage'

export type CarouselSlide = {
  type: 'identity' | 'before' | 'after' | 'cta' | 'proof_or_filter' | 'struggle' | 'method'
  headline: string
  body: string
  image_url?: string
  cta_text?: string
  cta_url?: string
}

export type CarouselBgPalette = 'brand' | 'accent' | 'teal' | 'yellow' | 'gray'

export interface CarouselAdPreviewCardProps {
  practitionerName?: string
  practitionerPhotoUrl?: string
  practitionerPhotoCutoutUrl?: string
  whoYouHelp?: string
  primaryOutcome?: string
  templateName?: string
  slides: CarouselSlide[]
  bgPalette?: CarouselBgPalette
  photoFocalX?: number
  photoFocalY?: number
  photoScale?: number
  photoFitMode?: 'cover' | 'contain'
  photoGradientSide?: 'left' | 'right'
  // How much of the image fades into gradient (0.05..0.55). Higher = more background visible.
  photoReveal?: number
  // Identity slide title treatment.
  identityTextLayout?: 'side_strap' | 'bottom_banner' | 'transparent'
  // When true (typically while Customize drawer is open), identity photo becomes draggable.
  isAdjustingIdentityPhoto?: boolean
  // Called while dragging on identity slide.
  onPhotoFocalChange?: (x: number, y: number) => void
  onCustomizeClick?: () => void
  // Image mode: 'original' or 'cutout'
  photoMode?: 'original' | 'cutout'
  // Whether to show shadow on photo
  photoShadow?: boolean
}

const pickNonEmpty = (...candidates: Array<string | undefined | null>) => {
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim().length > 0) return c
  }
  return ''
}

const paletteGradient = (palette: CarouselBgPalette) => {
  switch (palette) {
    case 'accent':
      return 'linear-gradient(to bottom right, var(--chakra-colors-blue-700), var(--chakra-colors-blue-400))'
    case 'teal':
      return 'linear-gradient(to bottom right, var(--chakra-colors-teal-700), var(--chakra-colors-teal-400))'
    case 'yellow':
      return 'linear-gradient(to bottom right, var(--chakra-colors-yellow-600), var(--chakra-colors-yellow-300))'
    case 'gray':
      return 'linear-gradient(to bottom right, var(--chakra-colors-gray-800), var(--chakra-colors-gray-500))'
    case 'brand':
    default:
      return 'linear-gradient(to bottom right, var(--chakra-colors-brand-700), var(--chakra-colors-brand-400))'
  }
}

const paletteAccent = (palette: CarouselBgPalette) => {
  switch (palette) {
    case 'accent':
      return { bar: 'blue.400', quote: 'blue.500', tint: 'blue.700' }
    case 'teal':
      return { bar: 'teal.400', quote: 'teal.500', tint: 'teal.700' }
    case 'yellow':
      return { bar: 'yellow.400', quote: 'yellow.600', tint: 'yellow.600' }
    case 'gray':
      return { bar: 'gray.300', quote: 'gray.400', tint: 'gray.800' }
    case 'brand':
    default:
      return { bar: 'brand.400', quote: 'brand.500', tint: 'brand.700' }
  }
}

const tokenVar = (token: string) => `var(--chakra-colors-${token.replace('.', '-')})`

export const CarouselAdPreviewCard: React.FC<CarouselAdPreviewCardProps> = ({
  practitionerName,
  practitionerPhotoUrl,
  practitionerPhotoCutoutUrl,
  whoYouHelp,
  primaryOutcome,
  templateName,
  slides,
  bgPalette = 'brand',
  photoFocalX,
  photoFocalY,
  photoScale,
  photoFitMode = 'cover',
  photoGradientSide = 'left',
  photoReveal,
  identityTextLayout = 'side_strap',
  isAdjustingIdentityPhoto = false,
  onPhotoFocalChange,
  onCustomizeClick,
  photoMode = 'original',
  photoShadow = false
}) => {
  const safeSlides = useMemo(() => slides?.filter(Boolean) ?? [], [slides])
  const [idx, setIdx] = useState(0)
  const mediaRef = useRef<HTMLDivElement | null>(null)
  const isDraggingPhotoRef = useRef(false)

  const current = safeSlides[idx]
  const total = safeSlides.length
  const isIdentitySlide = current?.type === 'identity'
  const defaultFocalX = 50
  const defaultFocalY = 50
  const defaultScale = 1
  // Header always uses prop
  const headerPhotoUrlRaw = pickNonEmpty(practitionerPhotoUrl)
  // Identity slide: use cutout if available and mode is 'cutout', otherwise use original
  const identitySlidePhotoUrlRaw = isIdentitySlide 
    ? (photoMode === 'cutout' && practitionerPhotoCutoutUrl 
        ? pickNonEmpty(practitionerPhotoCutoutUrl, practitionerPhotoUrl, current?.image_url)
        : pickNonEmpty(practitionerPhotoUrl, current?.image_url))
    : ''
  const backgroundImageUrl = pickNonEmpty(current?.image_url)
  
  // Use cached blob URLs to prevent re-fetching when signed URLs change
  const headerPhotoUrl = useCachedImage(headerPhotoUrlRaw) || ''
  const identitySlidePhotoUrl = useCachedImage(identitySlidePhotoUrlRaw) || ''

  const focalX = Math.min(100, Math.max(0, typeof photoFocalX === 'number' ? photoFocalX : defaultFocalX))
  const focalY = Math.min(100, Math.max(0, typeof photoFocalY === 'number' ? photoFocalY : defaultFocalY))
  const scale = Math.min(1.8, Math.max(0.8, typeof photoScale === 'number' ? photoScale : defaultScale))
  const revealStop = Math.min(0.55, Math.max(0.05, typeof photoReveal === 'number' ? photoReveal : 0.22))
  const isAdjustingPhoto = Boolean(isAdjustingIdentityPhoto && isIdentitySlide)
  const identityTextOnRight = photoGradientSide === 'right'
  const accent = paletteAccent(bgPalette)
  // Reuse the existing "reveal" knob to control how wide the readability scrim is.
  // Wider scrim makes text easier to read without hiding the photo.
  const scrimPct = `${Math.round(Math.min(0.9, Math.max(0.35, revealStop + 0.25)) * 100)}%`
  const identityScrimCss = identityTextOnRight
    ? `linear-gradient(to left, rgba(0,0,0,0.70) 0%, rgba(0,0,0,0.45) 28%, rgba(0,0,0,0.0) ${scrimPct})`
    : `linear-gradient(to right, rgba(0,0,0,0.70) 0%, rgba(0,0,0,0.45) 28%, rgba(0,0,0,0.0) ${scrimPct})`
  const tintColor = tokenVar(accent.tint)
  const identityTintCss = identityTextOnRight
    ? `linear-gradient(to left, ${tintColor} 0%, rgba(0,0,0,0.0) ${scrimPct})`
    : `linear-gradient(to right, ${tintColor} 0%, rgba(0,0,0,0.0) ${scrimPct})`

  const bannerHeadline = useMemo(() => {
    const fromSlide = pickNonEmpty(current?.headline)
    if (fromSlide) return fromSlide

    if (current?.type === 'identity') {
      const name = pickNonEmpty(practitionerName)
      if (name) return `Meet ${name}`
    }
    return ''
  }, [current?.headline, current?.type, practitionerName])

  const bannerBody = useMemo(() => {
    const fromSlide = pickNonEmpty(current?.body)
    if (fromSlide) return fromSlide

    if (current?.type === 'identity') {
      const lines = [
        whoYouHelp?.trim() ? `For: ${whoYouHelp}` : '',
        primaryOutcome?.trim() ? `Outcome: ${primaryOutcome}` : ''
      ].filter(Boolean)
      return lines.join('\n')
    }
    return ''
  }, [current?.body, current?.type, whoYouHelp, primaryOutcome])

  if (!current) {
    return (
      <Card.Root borderWidth="2px" borderColor="gray.200" bg="white" borderRadius="xl">
        <Card.Body p={6} textAlign="center">
          <Text color="gray.500">No carousel preview available</Text>
        </Card.Body>
      </Card.Root>
    )
  }

  return (
    <Card.Root
      borderWidth="2px"
      borderColor="brand.200"
      bg="white"
      borderRadius="xl"
      overflow="hidden"
      maxW={{ base: 'full', md: '500px' }}
      w="full"
      mx="auto"
      _hover={{ borderColor: 'brand.300', shadow: 'md' }}
      transition="all 0.2s"
    >
      <Card.Body p={0}>
        <VStack align="stretch" gap={0}>
          {/* Media area */}
          <Box
            position="relative"
            w="full"
            style={{ backgroundImage: paletteGradient(bgPalette) }}
            overflow="hidden"
            // Keep the preview compact so it fits without scrolling
            h={{ base: '320px', sm: '360px', md: '380px' }}
            pb={isIdentitySlide ? { base: 20, sm: 24, md: 28 } : 0}
            ref={mediaRef as any}
          >
            {/* Customize button (overlay on media/photo) */}
            {onCustomizeClick ? (
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <Box
                    as="button"
                    aria-label="Customize look"
                    position="absolute"
                    top={3}
                    left={3}
                    bg="white"
                    color="brand.700"
                    borderWidth="2px"
                    borderColor="brand.400"
                    borderRadius="md"
                    shadow="lg"
                    zIndex={10}
                    w="44px"
                    h="44px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    cursor="pointer"
                    transition="all 0.2s"
                    _hover={{ 
                      bg: 'brand.50', 
                      borderColor: 'brand.500', 
                      transform: 'scale(1.05)',
                      shadow: 'xl'
                    }}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      onCustomizeClick()
                    }}
                  >
                    <Icon as={FiEdit2} boxSize="20px" color="brand.700" />
                  </Box>
                </Tooltip.Trigger>
                <Tooltip.Positioner>
                  <Tooltip.Content
                    bg="gray.900"
                    color="white"
                    px={3}
                    py={2}
                    borderRadius="md"
                    fontSize="sm"
                    maxW="300px"
                  >
                    <Tooltip.Arrow>
                      <Tooltip.ArrowTip fill="gray.900" />
                    </Tooltip.Arrow>
                    Customize the visual appearance: adjust photo position, background colors, text layout, and more.
                  </Tooltip.Content>
                </Tooltip.Positioner>
              </Tooltip.Root>
            ) : null}

            {/* Slide counter */}
            <Box position="absolute" top={2} right={2} zIndex={5}>
              <Text
                fontSize="xs"
                color="gray.800"
                bg="rgba(255,255,255,0.85)"
                px={2}
                py={1}
                borderRadius="full"
              >
                {idx + 1}/{total}
              </Text>
            </Box>
            {/* Optional background image from slide (kept subtle) */}
            {!isIdentitySlide && backgroundImageUrl ? (
              <Image
                src={backgroundImageUrl}
                alt=""
                position="absolute"
                top={0}
                left={0}
                w="full"
                h="full"
                objectFit="cover"
                opacity={0.35}
                aria-hidden="true"
              />
            ) : null}

            {/* Light gradient glaze for readability (only when needed) */}
            {!isIdentitySlide ? (
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                style={{
                  backgroundImage: 'linear-gradient(to bottom, rgba(0,0,0,0.50), rgba(0,0,0,0.20))'
                }}
              />
            ) : null}

            {/* Identity slide hero photo (full bleed) */}
            {isIdentitySlide && identitySlidePhotoUrl ? (
              <Box
                position="absolute"
                inset={0}
                pointerEvents={isAdjustingPhoto ? 'auto' : 'none'}
                cursor={isAdjustingPhoto ? 'grab' : 'default'}
                onPointerDown={(e) => {
                  if (!isAdjustingPhoto) return
                  e.preventDefault()
                  if (!mediaRef.current) return
                  const rect = mediaRef.current.getBoundingClientRect()
                  const px = e.clientX - rect.left
                  const py = e.clientY - rect.top
                  const x = Math.min(100, Math.max(0, (px / rect.width) * 100))
                  const y = Math.min(100, Math.max(0, (py / rect.height) * 100))
                  onPhotoFocalChange?.(x, y)
                  isDraggingPhotoRef.current = true
                  ;(e.currentTarget as any).setPointerCapture?.(e.pointerId)
                }}
                onPointerMove={(e) => {
                  if (!isAdjustingPhoto) return
                  e.preventDefault()
                  if (!mediaRef.current) return
                  if (!isDraggingPhotoRef.current) return
                  const rect = mediaRef.current.getBoundingClientRect()
                  const px = e.clientX - rect.left
                  const py = e.clientY - rect.top
                  const x = Math.min(100, Math.max(0, (px / rect.width) * 100))
                  const y = Math.min(100, Math.max(0, (py / rect.height) * 100))
                  onPhotoFocalChange?.(x, y)
                }}
                onPointerUp={() => {
                  isDraggingPhotoRef.current = false
                }}
                onPointerCancel={() => {
                  isDraggingPhotoRef.current = false
                }}
              >
                <Box
                  as="img"
                  src={identitySlidePhotoUrl}
                  alt="Practitioner"
                  w="full"
                  h="full"
                  objectFit={photoFitMode}
                  style={{
                    // Use translation (not just crop) so reposition feels like moving the layer.
                    // Map focal % (0..100) to translate range (approx -30%..+30%).
                    objectPosition: '50% 50%',
                    transform: `translate(${Math.min(30, Math.max(-30, (focalX - 50) * 0.6))}%, ${Math.min(
                      22,
                      Math.max(-22, (focalY - 50) * 0.44)
                    )}%) scale(${scale})`,
                    transformOrigin: '50% 50%',
                    // Add drop shadow if enabled
                    filter: photoShadow ? 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.3))' : 'none'
                  }}
                />
              </Box>
            ) : null}

            {/* Identity slide palette wash (so bg color is visible even with full-bleed photo). */}
            {isIdentitySlide && identitySlidePhotoUrl ? (
              <Box
                position="absolute"
                inset={0}
                style={{ backgroundImage: paletteGradient(bgPalette) }}
                opacity={0.10}
                mixBlendMode="soft-light"
                pointerEvents="none"
              />
            ) : null}

            {/* Identity slide readability scrim (on text side). */}
            {isIdentitySlide && identitySlidePhotoUrl ? (
              <>
                <Box position="absolute" inset={0} style={{ backgroundImage: identityScrimCss }} pointerEvents="none" />
                <Box
                  position="absolute"
                  inset={0}
                  style={{ backgroundImage: identityTintCss }}
                  opacity={0.14}
                  pointerEvents="none"
                />
              </>
            ) : null}

            {/* Identity slide: bottom banner. Other slides: centered message card. */}
            {isIdentitySlide && !isAdjustingPhoto && (bannerHeadline || bannerBody) ? (
              identityTextLayout === 'bottom_banner' ? (
                <Box
                  position="absolute"
                  left={0}
                  right={0}
                  bottom={0}
                  px={4}
                  pt={4}
                  pb={{ base: 10, sm: 10, md: 12 }}
                  bg="rgba(255,255,255,0.90)"
                  backdropFilter="blur(12px)"
                  borderTopWidth="1px"
                  borderColor="whiteAlpha.700"
                  pointerEvents="none"
                >
                  <VStack align="stretch" gap={1}>
                    {bannerHeadline ? (
                      <Text fontSize="md" fontWeight="bold" color="gray.900" noOfLines={2}>
                        {bannerHeadline}
                      </Text>
                    ) : null}
                    {bannerBody ? (
                      <Text fontSize="sm" color="gray.800" whiteSpace="pre-wrap" noOfLines={3}>
                        {bannerBody}
                      </Text>
                    ) : null}
                  </VStack>
                </Box>
              ) : identityTextLayout === 'transparent' ? (
                <VStack
                  position="absolute"
                  bottom={{ base: 12, sm: 14, md: 16 }}
                  left={identityTextOnRight ? 'auto' : { base: 4, sm: 6, md: 8 }}
                  right={identityTextOnRight ? { base: 4, sm: 6, md: 8 } : 'auto'}
                  align={identityTextOnRight ? 'flex-end' : 'flex-start'}
                  gap={2}
                  maxW={{ base: 'calc(100% - 32px)', sm: '380px', md: '420px' }}
                  pointerEvents="none"
                >
                  {bannerHeadline ? (
                    <Text
                      fontSize={{ base: 'lg', sm: 'xl', md: '2xl' }}
                      fontWeight="bold"
                      color="white"
                      noOfLines={2}
                      textShadow="0 2px 8px rgba(0,0,0,0.75), 0 0 2px rgba(0,0,0,0.5)"
                      textAlign={identityTextOnRight ? 'right' : 'left'}
                    >
                      {bannerHeadline}
                    </Text>
                  ) : null}
                  {bannerBody ? (
                    <Text
                      fontSize={{ base: 'sm', sm: 'md', md: 'lg' }}
                      color="white"
                      whiteSpace="pre-wrap"
                      noOfLines={4}
                      textShadow="0 2px 8px rgba(0,0,0,0.75), 0 0 2px rgba(0,0,0,0.5)"
                      textAlign={identityTextOnRight ? 'right' : 'left'}
                    >
                      {bannerBody}
                    </Text>
                  ) : null}
                </VStack>
              ) : (
                <Box
                  position="absolute"
                  bottom={{ base: 12, sm: 14, md: 16 }}
                  left={identityTextOnRight ? 'auto' : 4}
                  right={identityTextOnRight ? 4 : 'auto'}
                  w={{ base: 'calc(100% - 32px)', sm: '340px', md: '360px' }}
                  maxW={{ base: 'calc(100% - 32px)', sm: '360px' }}
                  p={{ base: 4, md: 5 }}
                  bg="rgba(255,255,255,0.82)"
                  backdropFilter="blur(14px)"
                  borderWidth="1px"
                  borderColor="whiteAlpha.700"
                  borderRadius="2xl"
                  shadow="xl"
                  pointerEvents="none"
                >
                  <Box
                    position="absolute"
                    top={3}
                    bottom={3}
                    left={identityTextOnRight ? 'auto' : 2}
                    right={identityTextOnRight ? 2 : 'auto'}
                    w="4px"
                    borderRadius="full"
                    bg={accent.bar}
                    opacity={0.95}
                  />
                  <VStack align="stretch" gap={2} pl={identityTextOnRight ? 0 : 3} pr={identityTextOnRight ? 3 : 0}>
                    {bannerHeadline ? (
                      <Text fontSize="md" fontWeight="bold" color="gray.900" noOfLines={2}>
                        {bannerHeadline}
                      </Text>
                    ) : null}
                    {bannerBody ? (
                      <Text
                        fontSize="sm"
                        color="gray.800"
                        whiteSpace="pre-wrap"
                        noOfLines={4}
                        fontStyle="italic"
                        position="relative"
                        pl={5}
                        _before={{
                          content: '"“"',
                          position: 'absolute',
                          left: 0,
                          top: '-0.15em',
                          fontSize: '28px',
                          lineHeight: 1,
                          color: accent.quote,
                          opacity: 0.95
                        }}
                      >
                        {bannerBody}
                      </Text>
                    ) : null}
                  </VStack>
                </Box>
              )
            ) : null}

            {!isIdentitySlide && (bannerHeadline || bannerBody) ? (
              identityTextLayout === 'transparent' ? (
                <VStack
                  position="absolute"
                  left={4}
                  right={4}
                  top={{ base: 10, md: 12 }}
                  bottom={{ base: 10, md: 12 }}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  pointerEvents="none"
                  gap={2}
                  maxW="360px"
                  mx="auto"
                >
                  {bannerHeadline ? (
                    <Text
                      fontSize={{ base: 'xl', md: '2xl' }}
                      fontWeight="extrabold"
                      color="white"
                      lineHeight="shorter"
                      textAlign="center"
                      textShadow="0 2px 8px rgba(0,0,0,0.75), 0 0 2px rgba(0,0,0,0.5)"
                      noOfLines={2}
                    >
                      {bannerHeadline}
                    </Text>
                  ) : null}
                  {bannerBody ? (
                    <Text
                      fontSize={{ base: 'md', md: 'lg' }}
                      color="white"
                      whiteSpace="pre-wrap"
                      lineHeight="tall"
                      textAlign="center"
                      textShadow="0 2px 8px rgba(0,0,0,0.75), 0 0 2px rgba(0,0,0,0.5)"
                      noOfLines={4}
                    >
                      {bannerBody}
                    </Text>
                  ) : null}
                </VStack>
              ) : (
                <Box
                  position="absolute"
                  left={4}
                  right={4}
                  top={{ base: 10, md: 12 }}
                  bottom={{ base: 10, md: 12 }}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  pointerEvents="none"
                >
                  <Box
                    w="full"
                    maxW="360px"
                    p={5}
                    borderRadius="xl"
                    bg="whiteAlpha.900"
                    backdropFilter="blur(10px)"
                    shadow="xl"
                  >
                    <VStack align="stretch" gap={2}>
                      {bannerHeadline ? (
                        <Text fontSize="xl" fontWeight="extrabold" color="gray.900" lineHeight="shorter">
                          {bannerHeadline}
                        </Text>
                      ) : null}
                      {bannerBody ? (
                        <Text fontSize="md" color="gray.800" whiteSpace="pre-wrap" lineHeight="tall">
                          {bannerBody}
                        </Text>
                      ) : null}
                    </VStack>
                  </Box>
                </Box>
              )
            ) : null}

            {/* < > navigation */}
            <HStack
              position="absolute"
              top={0}
              left={0}
              right={0}
              h="full"
              justify="space-between"
              px={2}
              pointerEvents={isAdjustingPhoto ? 'none' : 'auto'}
            >
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIdx((v) => Math.max(0, v - 1))}
                isDisabled={idx === 0}
                bg="rgba(255,255,255,0.85)"
                _hover={{ bg: 'rgba(255,255,255,0.95)' }}
                aria-label="Previous slide"
                minW="36px"
                h="36px"
                p={0}
                pointerEvents="auto"
              >
                <Icon as={FiChevronLeft} />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIdx((v) => Math.min(total - 1, v + 1))}
                isDisabled={idx >= total - 1}
                bg="rgba(255,255,255,0.85)"
                _hover={{ bg: 'rgba(255,255,255,0.95)' }}
                aria-label="Next slide"
                minW="36px"
                h="36px"
                p={0}
                pointerEvents="auto"
              >
                <Icon as={FiChevronRight} />
              </Button>
            </HStack>

            {/* Dots */}
            <HStack
              position="absolute"
              bottom={isIdentitySlide ? 4 : 3}
              left={0}
              right={0}
              justify="center"
              gap={2.5}
              pointerEvents={isAdjustingPhoto ? 'none' : 'auto'}
              zIndex={isIdentitySlide ? 2 : 'auto'}
            >
              {safeSlides.map((_, i) => (
                <Box
                  key={i}
                  as="button"
                  type="button"
                  w={i === idx ? '10px' : '8px'}
                  h={i === idx ? '10px' : '8px'}
                  borderRadius="full"
                  bg={i === idx ? 'white' : 'rgba(255,255,255,0.7)'}
                  borderWidth={i === idx ? '2px' : '1.5px'}
                  borderColor={i === idx ? 'brand.400' : 'rgba(0,0,0,0.3)'}
                  shadow={i === idx ? 'md' : 'sm'}
                  transition="all 0.2s"
                  _hover={{
                    bg: i === idx ? 'white' : 'rgba(255,255,255,0.85)',
                    borderColor: i === idx ? 'brand.500' : 'rgba(0,0,0,0.4)',
                    transform: 'scale(1.2)'
                  }}
                  onClick={() => setIdx(i)}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </HStack>
          </Box>

          {/* CTA */}
          {current.type === 'cta' && (current.cta_text || current.cta_url) ? (
            <VStack align="stretch" gap={0} p={4}>
              <Button colorPalette="brand" w="full" as="a" href={current.cta_url || '#'} target="_blank">
                {current.cta_text || 'Book a call'}
              </Button>
            </VStack>
          ) : null}
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}





